import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash, Crown, ShieldCheck, User as UserIcon, LinkSimple, Truck, PencilSimple } from "@phosphor-icons/react";

const ROLE_META = {
  owner: { label: "Owner", icon: Crown, cls: "bg-signal/10 text-signal border-signal/30" },
  admin: { label: "Admin", icon: ShieldCheck, cls: "bg-signal/10 text-signal border-signal/30" },
  user: { label: "User", icon: UserIcon, cls: "bg-muted text-muted-foreground border-border" },
  // Porter: sees every order (no salesman-mapping restriction, like Admin/Owner) but can
  // only upload delivery receipts — no delete, no mark-as-delivered.
  porter: { label: "Porter", icon: Truck, cls: "bg-amber-500/10 text-amber-600 border-amber-500/30" },
};

function RoleBadge({ role }) {
  const meta = ROLE_META[role] || ROLE_META.user;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${meta.cls}`}>
      <Icon size={13} weight="bold" /> {meta.label}
    </span>
  );
}

export default function Users() {
  const { user: me, refreshUser } = useAuth();
  // Owner and Admin both have full access on this page — edit anyone's details, change
  // anyone's role, delete anyone (except the Owner account itself, which the backend
  // always protects). Regular Users and Porters get none of this.
  const canManage = me?.role === "owner" || me?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [salesmenOptions, setSalesmenOptions] = useState([]);
  const [mappingDraft, setMappingDraft] = useState({}); // { [userId]: string }
  const [mappingBusy, setMappingBusy] = useState({}); // { [userId]: bool }

  // Click-to-edit for name/email/phone/password. Only rendered when the viewer can manage
  // (Owner or Admin) — but then available on EVERY row, not just their own.
  // `editing` tracks which row+field is currently open; closed by default.
  const [editing, setEditing] = useState(null); // { userId, field } | null
  const [draftValue, setDraftValue] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const MAX_USERS = 5;
  const regularCount = items.filter((u) => u.role !== "admin" && u.role !== "owner").length;

  const load = () => {
    setLoading(true);
    return api.get("/users").then((r) => { setItems(r.data.items || []); return r.data.items || []; })
      .catch((err) => { toast.error(formatErr(err)); return items; })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Existing salesman names already used in sales data — makes it easy to map a user
    // to a name that actually has records, instead of typing it blind.
    api.get("/sales/salesmen").then((r) => setSalesmenOptions(r.data.items || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMapping = async (u) => {
    const value = (mappingDraft[u.id] ?? u.salesman_name ?? "").trim();
    setMappingBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await api.patch(`/users/${u.id}/salesman-mapping`, { salesman_name: value || null });
      toast.success(value ? `${u.name || u.email} is now mapped to "${value}"` : `Mapping cleared for ${u.name || u.email}`);
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setMappingBusy((b) => ({ ...b, [u.id]: false }));
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.phone) { toast.error("Email, password and phone number are required."); return; }
    const emailToAdd = form.email.trim().toLowerCase();
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success(`User added — ${regularCount + 1}/${MAX_USERS}`);
      setForm({ name: "", email: "", password: "", phone: "" });
      load();
    } catch (err) {
      if (err.isNetworkError) {
        // Connection dropped before the response came back — the user may have
        // still been created server-side. Re-check the live list before
        // showing an error, instead of leaving the confusing "already exists"
        // for a follow-up click.
        const fresh = await load();
        const alreadyThere = fresh.some((u) => u.email?.toLowerCase() === emailToAdd);
        if (alreadyThere) {
          toast.success("User was added (connection was just slow to confirm).");
          setForm({ name: "", email: "", password: "", phone: "" });
        } else {
          toast.error("Connection dropped and the user wasn't created — please try again.");
        }
      } else {
        toast.error(formatErr(err));
      }
    } finally { setBusy(false); }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User removed.");
      load();
    } catch (err) {
      if (err.isNetworkError) {
        const fresh = await load();
        if (!fresh.some((x) => x.id === u.id)) toast.success("User was removed (connection was just slow to confirm).");
        else toast.error("Connection dropped — please try again.");
      } else {
        toast.error(formatErr(err));
      }
    }
  };

  const openEdit = (u, field, currentValue) => {
    setEditing({ userId: u.id, field });
    setDraftValue(field === "password" ? "" : currentValue || "");
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = async (u) => {
    const field = editing?.field;
    const value = draftValue.trim();
    if (!value) { toast.error("This field can't be empty."); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/users/${u.id}/profile`, { [field]: value });
      const label = field === "password" ? "Password" : field === "name" ? "Name" : field === "phone" ? "Phone number" : "Email";
      toast.success(`${label} updated for ${u.name || u.email}.`);
      setEditing(null);
      if (u.id === me?.id) await refreshUser(); // editing our own account — refresh the header too
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSavingEdit(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}/role`, { role });
      toast.success(`${u.email} is now ${ROLE_META[role].label}.`);
      load();
    } catch (err) {
      if (err.isNetworkError) {
        const fresh = await load();
        const updated = fresh.find((x) => x.id === u.id);
        if (updated?.role === role) toast.success(`${u.email} is now ${ROLE_META[role].label} (connection was just slow to confirm).`);
        else toast.error("Connection dropped — please try again.");
      } else {
        toast.error(formatErr(err));
      }
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="users-page">
      <datalist id="salesmen-options">
        {salesmenOptions.map((s) => <option key={s} value={s} />)}
      </datalist>
      <div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight">Team & Roles</h1>
        <p className="text-base text-muted-foreground mt-1">
          1 Owner, 1 Admin, up to {MAX_USERS} Users — {regularCount}/{MAX_USERS} regular users used.
        </p>
      </div>

      <form onSubmit={addUser} className="border border-border rounded-lg p-6 bg-card space-y-4">
        <h2 className="font-bold text-lg">Add a new user</h2>
        <div className="grid sm:grid-cols-4 gap-4">
          <input className="border border-border rounded-md px-4 py-3 text-base bg-background" placeholder="Name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="border border-border rounded-md px-4 py-3 text-base bg-background" placeholder="Email" type="email"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input className="border border-border rounded-md px-4 py-3 text-base bg-background" placeholder="Phone number" type="tel"
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input className="border border-border rounded-md px-4 py-3 text-base bg-background" placeholder="Password" type="password"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        <button type="submit" disabled={busy || regularCount >= MAX_USERS}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-signal text-white text-base font-bold disabled:opacity-50">
          <UserPlus size={16} weight="bold" /> {regularCount >= MAX_USERS ? "User limit reached" : "Add user"}
        </button>
      </form>

      <div className="border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-6 text-base text-muted-foreground">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead className="bg-muted/50 text-sm uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Phone</th>
                <th className="text-left px-5 py-3">Role</th>
                <th className="text-left px-5 py-3">Salesman Mapping</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-border">
                  <td className="px-5 py-3">
                    {canManage ? (
                      editing?.userId === u.id && editing?.field === "name" ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            className="border border-border rounded-md px-2.5 py-1.5 text-base bg-background w-32"
                            value={draftValue}
                            onChange={(e) => setDraftValue(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && saveEdit(u)}
                          />
                          <button onClick={() => saveEdit(u)} disabled={savingEdit}
                            className="text-sm font-bold text-signal disabled:opacity-50">Save</button>
                          <button onClick={cancelEdit} className="text-sm text-muted-foreground">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => openEdit(u, "name", u.name)}
                          title="Click to edit name"
                          className="inline-flex items-center gap-1.5 text-left hover:underline decoration-dotted underline-offset-2">
                          {u.name} <PencilSimple size={12} className="text-muted-foreground" />
                        </button>
                      )
                    ) : (
                      u.name
                    )}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">
                    {canManage ? (
                      editing?.userId === u.id && editing?.field === "email" ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            type="email"
                            className="border border-border rounded-md px-2.5 py-1.5 text-base bg-background w-48 text-foreground"
                            value={draftValue}
                            onChange={(e) => setDraftValue(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && saveEdit(u)}
                          />
                          <button onClick={() => saveEdit(u)} disabled={savingEdit}
                            className="text-sm font-bold text-signal disabled:opacity-50">Save</button>
                          <button onClick={cancelEdit} className="text-sm text-muted-foreground">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => openEdit(u, "email", u.email)}
                          title="Click to edit email"
                          className="inline-flex items-center gap-1.5 text-left hover:underline decoration-dotted underline-offset-2">
                          {u.email} <PencilSimple size={12} />
                        </button>
                      )
                    ) : (
                      u.email
                    )}
                  </td>
                  <td className="px-5 py-3 text-muted-foreground">
                    {canManage ? (
                      editing?.userId === u.id && editing?.field === "phone" ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            autoFocus
                            type="tel"
                            className="border border-border rounded-md px-2.5 py-1.5 text-base bg-background w-32 text-foreground"
                            value={draftValue}
                            onChange={(e) => setDraftValue(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && saveEdit(u)}
                          />
                          <button onClick={() => saveEdit(u)} disabled={savingEdit}
                            className="text-sm font-bold text-signal disabled:opacity-50">Save</button>
                          <button onClick={cancelEdit} className="text-sm text-muted-foreground">Cancel</button>
                        </div>
                      ) : (
                        <button onClick={() => openEdit(u, "phone", u.phone)}
                          title="Click to edit phone number"
                          className="inline-flex items-center gap-1.5 text-left hover:underline decoration-dotted underline-offset-2">
                          {u.phone || "Add phone"} <PencilSimple size={12} />
                        </button>
                      )
                    ) : (
                      u.phone || "—"
                    )}
                  </td>
                  <td className="px-5 py-3"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-3">
                    {u.role === "user" ? (
                      // Only plain Users are mapped to one salesman. Porters (like
                      // Admin/Owner) always see every order, so no mapping applies to them.
                      <div className="flex items-center gap-2">
                        <input
                          list="salesmen-options"
                          className="border border-border rounded-md px-3 py-2 text-sm bg-background w-40"
                          placeholder="e.g. Anmol"
                          value={mappingDraft[u.id] ?? u.salesman_name ?? ""}
                          onChange={(e) => setMappingDraft((d) => ({ ...d, [u.id]: e.target.value }))}
                        />
                        <button
                          onClick={() => saveMapping(u)}
                          disabled={mappingBusy[u.id]}
                          title="Save mapping — this user will only see this salesman's data"
                          className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted inline-flex items-center gap-1 disabled:opacity-50"
                        >
                          <LinkSimple size={12} weight="bold" /> {mappingBusy[u.id] ? "..." : "Save"}
                        </button>
                        {u.salesman_name && (
                          <span className="text-xs text-signal">✓ mapped</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">— full access —</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      {canManage && (
                        editing?.userId === u.id && editing?.field === "password" ? (
                          <div className="flex items-center gap-1.5">
                            <input
                              autoFocus
                              type="password"
                              placeholder="New password"
                              className="border border-border rounded-md px-2.5 py-2 text-sm bg-background w-32"
                              value={draftValue}
                              onChange={(e) => setDraftValue(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && saveEdit(u)}
                            />
                            <button onClick={() => saveEdit(u)} disabled={savingEdit}
                              className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted disabled:opacity-50">
                              Save
                            </button>
                            <button onClick={cancelEdit}
                              className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted">
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => openEdit(u, "password", "")}
                            className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted inline-flex items-center gap-1.5">
                            <PencilSimple size={12} weight="bold" /> Edit password
                          </button>
                        )
                      )}
                      {canManage && u.role === "user" && (
                        <button onClick={() => changeRole(u, "admin")}
                          className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted">
                          Make Admin
                        </button>
                      )}
                      {canManage && u.role === "admin" && (
                        <button onClick={() => changeRole(u, "user")}
                          className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted">
                          Demote to User
                        </button>
                      )}
                      {canManage && u.role === "user" && (
                        <button onClick={() => changeRole(u, "porter")}
                          className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted">
                          Make Porter
                        </button>
                      )}
                      {canManage && u.role === "porter" && (
                        <button onClick={() => changeRole(u, "user")}
                          className="text-sm font-bold px-3.5 py-2 rounded-md border border-border hover:bg-muted">
                          Demote to User
                        </button>
                      )}
                      {u.role !== "owner" && u.role !== "admin" && canManage && (
                        <button onClick={() => removeUser(u)}
                          className="text-sm font-bold p-2.5 rounded-md border border-border hover:bg-destructive/10 text-destructive">
                          <Trash size={14} />
                        </button>
                      )}
                      {canManage && u.role === "admin" && (
                        <button onClick={() => removeUser(u)}
                          className="text-sm font-bold p-2.5 rounded-md border border-border hover:bg-destructive/10 text-destructive">
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
